import React from 'react';
import Select from './Select';

/**
 * Reusable Form Field with Floating Label (on border) style
 */
const FormField = ({ 
    label, 
    name, 
    type = 'text', 
    value, 
    onChange, 
    placeholder, 
    options = [], 
    className = '', 
    size = 'md',
    rows = 4,
    required = false,
    inputRef,
    ...props
}) => {
    const isSelect = type === 'select';
    const isTextarea = type === 'textarea';

    const containerClasses = `relative ${className}`;
    
    // Label sits on the border. We use bg-white/dark:bg-[#262b33] to hide the border line behind the text.
    const labelClasses = "absolute -top-2.5 left-3 bg-white dark:bg-[#262b33] px-1.5 text-[12px] font-semibold text-blue-600 dark:text-blue-400 transition-all z-10 pointer-events-none";
    
    // size="lg" pins the control to a fixed height so text inputs and selects
    // in the same grid row are exactly the same size.
    const heightClasses = size === 'lg' ? 'h-[46px] py-0' : 'py-3';
    const inputClasses = `w-full border border-gray-200 dark:border-gray-800 rounded-lg px-4 ${heightClasses} text-[14px] text-gray-700 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all bg-transparent`;

    // Normalize whatever option shape callers pass (string | {value,label} | {value,text})
    // into the { value, label } pairs the shared Select expects.
    const selectOptions = (options || []).map((opt) =>
        opt && typeof opt === 'object'
            ? { value: opt.value, label: opt.label ?? opt.text ?? String(opt.value) }
            : { value: opt, label: String(opt) }
    );

    return (
        <div className={containerClasses}>
            {label && (
                <label htmlFor={name} className={labelClasses}>
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            
            {isSelect ? (
                <Select
                    value={value}
                    onChange={(v) => onChange && onChange({ target: { name, value: v } })}
                    options={selectOptions}
                    placeholder={placeholder}
                    disabled={props.disabled}
                    size={size}
                    className="w-full"
                />
            ) : isTextarea ? (
                <textarea
                    id={name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    rows={rows}
                    className={`w-full border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-3 text-[14px] text-gray-700 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all bg-transparent`}
                    required={required}
                    {...props}
                />
            ) : (
                <input
                    ref={inputRef}
                    id={name}
                    name={name}
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className={inputClasses}
                    required={required}
                    {...props}
                />
            )}
        </div>
    );
};

export default FormField;
